if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Local success flag — set to true only when the add actually succeeded.
        // Using `this.error` isn't reliable because it's only set for sold-out errors,
        // not for generic request failures.
        let _didSucceed = false;

        // ── Feedback text flow ─────────────────────────────────────
        // If a previous "Added to Bag ✓" is still visible, cancel it and
        // restore the true original text before capturing again — otherwise
        // a rapid second click would capture "Added to Bag ✓" as the original.
        if (this._feedbackTimer) {
          clearTimeout(this._feedbackTimer);
          this._feedbackTimer = null;
          if (this._originalAtcText != null) {
            this.submitButtonText.textContent = this._originalAtcText;
          }
          this.submitButton.classList.remove('is-added');
        }
        this._originalAtcText = this.submitButtonText.textContent;
        this.submitButtonText.textContent = 'Adding...';

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Add succeeded — flag it so .finally() can show "Added to Bag ✓"
            _didSucceed = true;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            // Success feedback: briefly show "Added to Bag ✓", then restore.
            // Any non-success path (error, catch, redirect) restores the original text immediately.
            if (!_didSucceed) {
              this.submitButtonText.textContent = this._originalAtcText;
              this._originalAtcText = null;
            } else {
              this.submitButtonText.textContent = 'Added to Bag ✓';
              this.submitButton.classList.add('is-added');
              this._feedbackTimer = setTimeout(() => {
                // Guard: only restore if we're still showing the feedback
                // (variant change or a fresh click may have already reset it)
                if (this.submitButton.classList.contains('is-added')) {
                  this.submitButtonText.textContent = this._originalAtcText;
                  this.submitButton.classList.remove('is-added');
                }
                this._originalAtcText = null;
                this._feedbackTimer = null;
              }, 1400);
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          const _variantData = this.form.closest('product-info')?.querySelector('[data-selected-variant]');
          let _price = '';
          if (_variantData) {
            try {
              const _v = JSON.parse(_variantData.textContent);
              if (_v?.price) {
                _price = ' - ' + (window.Shopify?.formatMoney
                  ? Shopify.formatMoney(_v.price, window.theme?.moneyFormat || '₹{{amount}}')
                  : '₹' + Math.round(_v.price / 100).toLocaleString('en-IN'));
              }
            } catch(e) {}
          }
          this.submitButtonText.textContent = 'Add To Bag' + _price;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
